import {SingleSignOnBanner} from '@github-ui/single-sign-on-banner'
import {AlertIcon, type Icon} from '@primer/octicons-react'
import {Blankslate} from '@primer/react/experimental'
import {type ReactNode, type Ref, useId} from 'react'

import styles from './ErrorState.module.css'

export interface ErrorStatePrimaryAction {
  href: string
  label: string
  onSelect?: () => void
}

export interface ErrorStateProps {
  title: string
  description?: ReactNode
  /** Primary recovery action rendered with the Blankslate button treatment. */
  primaryAction?: ErrorStatePrimaryAction
  /** Logins of organizations to offer single sign-on for, rendered with the shared SSO banner. */
  ssoOrgs?: string[]
  icon?: Icon
  testId?: string
  /**
   * Visually hidden page heading, for hosts that render this state as the whole
   * page. Hosts that already have a page heading leave it unset.
   */
  srHeading?: string
  /**
   * Makes the state a focus target, named by its title and described by its
   * description, for hosts that move focus to it when it replaces what had focus.
   */
  focusRef?: Ref<HTMLDivElement>
}

/**
 * Full-panel error state shared by Copilot Chat v1 and agent sessions, with an
 * optional single sign-on re-prompt for the given organizations.
 */
export function ErrorState({
  title,
  description,
  primaryAction,
  ssoOrgs,
  icon: IconComponent = AlertIcon,
  testId,
  srHeading,
  focusRef,
}: ErrorStateProps) {
  const showSSO = ssoOrgs && ssoOrgs.length > 0
  const headingId = useId()
  const descriptionId = useId()
  const hasDescription = description !== undefined
  const handlePrimaryActionClick = (event: React.MouseEvent<HTMLElement>) => {
    if (
      primaryAction?.onSelect &&
      event.target instanceof Element &&
      event.target.closest('a')?.getAttribute('href') === primaryAction.href
    ) {
      primaryAction.onSelect()
    }
  }

  return (
    <div
      ref={focusRef}
      className={styles.container}
      data-testid={testId}
      {...(focusRef && {
        tabIndex: -1,
        role: 'group',
        'aria-labelledby': headingId,
        'aria-describedby': hasDescription ? descriptionId : undefined,
      })}
    >
      {srHeading && <h1 className="sr-only">{srHeading}</h1>}
      <Blankslate border={false} onClickCapture={primaryAction?.onSelect ? handlePrimaryActionClick : undefined}>
        <Blankslate.Visual>
          <IconComponent size="medium" className={styles.icon} />
        </Blankslate.Visual>
        <Blankslate.Heading as="h2" id={headingId}>
          {title}
        </Blankslate.Heading>
        {hasDescription && <Blankslate.Description id={descriptionId}>{description}</Blankslate.Description>}
        {primaryAction && (
          <Blankslate.PrimaryAction href={primaryAction.href}>{primaryAction.label}</Blankslate.PrimaryAction>
        )}
      </Blankslate>
      {showSSO && (
        <div className={styles.sso}>
          <SingleSignOnBanner protectedOrgs={ssoOrgs} />
        </div>
      )}
    </div>
  )
}
